// ============================================================================
// usePostings — Personal Finance V2 (Blueprint §5.2)
// Hook de carga Firestore + CRUD para postings e templates.
// Extraído de PersonalFinance.jsx para desacoplar lógica de dados da UI.
// ============================================================================

import { useState, useEffect, useCallback } from "react";
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { settlePair } from "../services/postingService.js";
import {
  loadPersonalFinanceSettings,
  loadTemplates,
  loadPostingsByMonth,
  loadPostingsByYear,
  updatePosting,
  createTemplate,
  createPosting,
  createChildPosting,
  deletePosting as deletePostingRepo,
  saveCategories as saveCategoriesRepo,
  saveInitialBalance as saveInitialBalanceRepo,
  saveTemplateLabel as saveTemplateLabelRepo,
  addSubcategory as addSubcategoryRepo,
  addGroup as addGroupRepo,
  addSudValue as addSudValueRepo,
  addNatureza as addNaturezaRepo,
  linkBlingId as linkBlingIdRepo,
  markPostingPaid,
  migrateLegacyIsPaid,
  cancelUnimedPostings,
} from "../../../../services/PostingRepository.js";
import { DEFAULT_CATEGORIES } from "../domain/categories.js";

/** Normaliza docs legados (pré-V2):
 *  1. isPaid:true  — campo booleano do código antigo
 *  2. settlementDate setado mas status !== 'PAID'  — código que gravava a data mas esquecia o status */
function normalizeLegacyDoc(raw) {
  const protected_ =
    raw.status === "PAID" ||
    raw.status === "CANCELLED" ||
    raw.status === "FORECAST";
  if (protected_) return raw;
  const isLegacyPaid = raw.isPaid === true || Boolean(raw.settlementDate);
  if (isLegacyPaid) {
    return {
      ...raw,
      status: "PAID",
      settlementDate: raw.settlementDate || raw.date,
    };
  }
  return raw;
}

/** Segunda passagem: se a DESPESA do par está PAID, sincroniza a RECEITA (Retirada) para PAID também.
 *  Cobre o caso legado em que o income não tinha settlementDate copiado. */
function normalizePairStatus(docs) {
  const expenseByPair = new Map();
  docs.forEach((d) => {
    if (d.pairId && d.group !== "RECEITAS" && d.status === "PAID") {
      expenseByPair.set(d.pairId, d.settlementDate || d.date);
    }
  });
  if (expenseByPair.size === 0) return docs;
  return docs.map((d) => {
    if (
      d.pairId &&
      d.group === "RECEITAS" &&
      d.status !== "PAID" &&
      d.status !== "CANCELLED"
    ) {
      const sd = expenseByPair.get(d.pairId);
      if (sd !== undefined) return { ...d, status: "PAID", settlementDate: sd };
    }
    return d;
  });
}

export function usePostings({ currentDate, filterMode, yearViewMode, view }) {
  const queryClient = useQueryClient();
  const currentYear = currentDate.getFullYear();

  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [sud, setSud] = useState({ nivel1: [], nivel2: [], nivel3: [] });
  const [naturezas, setNaturezas] = useState(["PF", "PJ"]);
  const [initialBalance, setInitialBalance] = useState(0);

  // Query: Settings
  const { data: settingsData } = useQuery({
    queryKey: ["personalFinanceSettings"],
    queryFn: loadPersonalFinanceSettings,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (settingsData) {
      if (settingsData.categories) setCategories(settingsData.categories);
      if (settingsData.sud) setSud(settingsData.sud);
      if (settingsData.naturezas) setNaturezas(settingsData.naturezas);
      if (settingsData.initialBalance)
        setInitialBalance(Number(settingsData.initialBalance));
    }
  }, [settingsData]);

  // Mutações de taxonomia (SSOT no Firestore — ver Art. da migração 17/09/2026)
  const invalidateSettings = () =>
    queryClient.invalidateQueries({ queryKey: ["personalFinanceSettings"] });

  const addSubcategoryMutation = useMutation({
    mutationFn: ({ group, value }) => addSubcategoryRepo(group, value),
    onSuccess: invalidateSettings,
  });
  const addSubcategory = useCallback(
    (group, value) => addSubcategoryMutation.mutateAsync({ group, value }),
    [addSubcategoryMutation],
  );

  const addGroupMutation = useMutation({
    mutationFn: ({ key, label }) => addGroupRepo(key, label),
    onSuccess: invalidateSettings,
  });
  const addGroup = useCallback(
    (key, label) => addGroupMutation.mutateAsync({ key, label }),
    [addGroupMutation],
  );

  const addSudValueMutation = useMutation({
    mutationFn: ({ nivel, value }) => addSudValueRepo(nivel, value),
    onSuccess: invalidateSettings,
  });
  const addSudValue = useCallback(
    (nivel, value) => addSudValueMutation.mutateAsync({ nivel, value }),
    [addSudValueMutation],
  );

  const addNaturezaMutation = useMutation({
    mutationFn: (value) => addNaturezaRepo(value),
    onSuccess: invalidateSettings,
  });
  const addNatureza = useCallback(
    (value) => addNaturezaMutation.mutateAsync(value),
    [addNaturezaMutation],
  );

  // Query: Templates
  const { data: templatesData } = useQuery({
    queryKey: ["templates"],
    queryFn: loadTemplates,
    staleTime: 30000,
  });

  // Query: Postings by Month
  const {
    data: monthPostings,
    isLoading: monthLoading,
    isPlaceholderData: monthIsPlaceholder,
    refetch: refetchMonth,
  } = useQuery({
    queryKey: [
      "postingsByMonth",
      currentDate.getFullYear(),
      currentDate.getMonth(),
      filterMode,
    ],
    queryFn: () =>
      loadPostingsByMonth(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        filterMode,
      ),
    enabled: view !== "YEARLY",
    staleTime: 30000,
    // Ao trocar de mês, mantém o mês anterior na tela até o novo chegar —
    // sem isso a tela inteira virava "carregando", a página encolhia e a
    // rolagem voltava ao topo (achado do titular, 18/09/2026).
    placeholderData: keepPreviousData,
  });

  // Query: Postings by Year
  const {
    data: yearPostingsData,
    isLoading: yearLoading,
    refetch: refetchYear,
  } = useQuery({
    queryKey: ["postingsByYear", currentYear, yearViewMode],
    queryFn: () => loadPostingsByYear(currentYear, yearViewMode),
    enabled: view === "YEARLY",
    staleTime: 30000,
  });

  const postings =
    view !== "YEARLY"
      ? normalizePairStatus(
          (monthPostings || []).map((d) => normalizeLegacyDoc(d)),
        )
      : [];
  const yearPostings =
    view === "YEARLY"
      ? normalizePairStatus(
          (yearPostingsData || []).map((d) => normalizeLegacyDoc(d)),
        )
      : [];
  const templates = templatesData || [];
  const loading = view === "YEARLY" ? yearLoading : monthLoading;
  const refreshing = view !== "YEARLY" && monthIsPlaceholder;

  const loadTransactions = useCallback(() => {
    refetchMonth();
  }, [refetchMonth]);

  const loadYearlyData = useCallback(() => {
    refetchYear();
  }, [refetchYear]);

  // Art. 188-G — Migração background
  useEffect(() => {
    const FLAG = "rda_art188g_legacy_ispaid_migration_v1";
    if (localStorage.getItem(FLAG)) return;
    (async () => {
      try {
        const migrated = await migrateLegacyIsPaid();
        if (migrated === 0) {
          localStorage.setItem(FLAG, "1");
          return;
        }
        console.log(
          `[Art.188-G] ${migrated} docs legados migrados isPaid→status:PAID`,
        );
        localStorage.setItem(FLAG, "1");
        queryClient.invalidateQueries({ queryKey: ["postingsByMonth"] });
        queryClient.invalidateQueries({ queryKey: ["postingsByYear"] });
      } catch (e) {
        console.warn("[Art.188-G] Migração legacy isPaid falhou:", e.message);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Art. 187 — Auto-cancelamento único doc Unimed
  useEffect(() => {
    const FLAG = "rda_art187_unimed_cancelled_v1";
    if (localStorage.getItem(FLAG)) return;
    (async () => {
      try {
        const cancelled = await cancelUnimedPostings();
        if (cancelled > 0) {
          queryClient.invalidateQueries({ queryKey: ["postingsByMonth"] });
          queryClient.invalidateQueries({ queryKey: ["postingsByYear"] });
        }
        localStorage.setItem(FLAG, "1");
      } catch (e) {
        console.warn("[Art.187] Erro ao cancelar Unimed:", e.message);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Mutations ---
  const invalidatePostings = () => {
    queryClient.invalidateQueries({ queryKey: ["postingsByMonth"] });
    queryClient.invalidateQueries({ queryKey: ["postingsByYear"] });
    queryClient.invalidateQueries({ queryKey: ["templates"] });
  };

  // Cria template de recorrência (artigo 188-F)
  const saveTemplateMutation = useMutation({
    mutationFn: async (tplData) => {
      await createTemplate({
        description: tplData.descIn,
        amount: tplData.amount,
        type: tplData.group,
        subCategory: tplData.sub,
        account: tplData.account,
        // Achado 17/09/2026: o SUD era capturado no modal mas descartado
        // aqui, silenciosamente, antes de chegar no Firestore.
        sudNivel1: tplData.suds?.nivel1 || null,
        sudNivel2: tplData.suds?.nivel2 || null,
        sudNivel3: tplData.suds?.nivel3 || null,
        sudVars: tplData.suds?.vars || null,
        anchorDay: new Date().getDate(),
        active: true,
      });
    },
    onSuccess: () => invalidatePostings(),
  });

  const saveTemplate = useCallback(
    async (tplData) => {
      await saveTemplateMutation.mutateAsync(tplData);
    },
    [saveTemplateMutation],
  );

  // Salva label personalizado de template (↻ Firestore persistente)
  const saveTemplateLabelMutation = useMutation({
    mutationFn: async ({ templateId, newLabel, sudInfo }) => {
      await saveTemplateLabelRepo(templateId, newLabel, sudInfo);
    },
    onSuccess: () => invalidatePostings(),
  });

  const saveTemplateLabel = useCallback(
    async (templateId, newLabel, sudInfo) => {
      await saveTemplateLabelMutation.mutateAsync({
        templateId,
        newLabel,
        sudInfo,
      });
    },
    [saveTemplateLabelMutation],
  );

  const savePostingMutation = useMutation({
    mutationFn: async ({ formData, mode }) => {
      // Editar um lançamento já existente é sempre uma atualização normal
      // do próprio documento — mesmo que ele tenha se originado como
      // "Recorrente" (modoLancamento é só um rótulo histórico no posting,
      // não vincula esse documento a um template pra editar em bloco).
      // Achado real 18/09/2026: antes disso, editar um lançamento
      // "Recorrente" caía no branch de baixo e criava um TEMPLATE NOVO em
      // vez de atualizar o documento — a edição "sumia" sem erro nenhum.
      if (formData.id) {
        await updatePosting(formData.id, formData);
        return;
      }
      if (mode === "RECURRING") {
        await createTemplate(formData);
        // Achado real 18/09/2026: o campo "Repetições" da tela sempre
        // existiu e o titular sempre pôde preencher, mas nada gerava os
        // meses seguintes de verdade — só o mês da criação virava
        // lançamento real. Agora createPosting() em modo RECURRING gera os
        // `repeatCount` meses pedidos (respeitando Mensal/Bimestral/etc.).
        await createPosting({ ...formData, forceSingle: true }, "RECURRING");
      } else {
        await createPosting(formData, mode);
      }
    },
    onSuccess: () => invalidatePostings(),
  });

  const savePosting = useCallback(
    async (formData, mode) => {
      await savePostingMutation.mutateAsync({ formData, mode });
    },
    [savePostingMutation],
  );

  const deletePostingMutation = useMutation({
    mutationFn: ({ id, isTemplate }) => deletePostingRepo(id, isTemplate),
    onSuccess: () => invalidatePostings(),
  });

  const deletePosting = useCallback(
    async (id, isTemplate = false) => {
      await deletePostingMutation.mutateAsync({ id, isTemplate });
    },
    [deletePostingMutation],
  );

  const saveCategoriesMutation = useMutation({
    mutationFn: saveCategoriesRepo,
    onSuccess: (_, newCats) => {
      setCategories(newCats);
      queryClient.invalidateQueries({ queryKey: ["personalFinanceSettings"] });
    },
  });

  const saveCategories = useCallback(
    async (newCats) => {
      await saveCategoriesMutation.mutateAsync(newCats);
    },
    [saveCategoriesMutation],
  );

  const saveInitialBalanceMutation = useMutation({
    mutationFn: saveInitialBalanceRepo,
    onSuccess: (_, newBalance) => {
      setInitialBalance(Number(newBalance));
      queryClient.invalidateQueries({ queryKey: ["personalFinanceSettings"] });
    },
  });

  const saveInitialBalance = useCallback(
    async (newBalance) => {
      await saveInitialBalanceMutation.mutateAsync(newBalance);
    },
    [saveInitialBalanceMutation],
  );

  const linkBlingIdMutation = useMutation({
    mutationFn: ({ postingId, blingId }) => linkBlingIdRepo(postingId, blingId),
    onSuccess: () => invalidatePostings(),
  });

  const linkBlingId = useCallback(
    async (postingId, blingId) => {
      await linkBlingIdMutation.mutateAsync({ postingId, blingId });
    },
    [linkBlingIdMutation],
  );

  const markPaidMutation = useMutation({
    mutationFn: async ({ item, settlementDate }) => {
      if (!item || !item.id || item.fonte !== "firestore") return;
      const effectiveDate =
        settlementDate || new Date().toISOString().split("T")[0];
      const allPostings = view === "YEARLY" ? yearPostings : postings;
      const posting = allPostings.find((p) => p.id === item.id);
      if (posting?.pairId) {
        await settlePair(posting.pairId, { settlementDate: effectiveDate });
      } else {
        await markPostingPaid(item.id, effectiveDate);
      }
    },
    onSuccess: () => invalidatePostings(),
  });

  const markPaid = useCallback(
    async (item, settlementDate) => {
      await markPaidMutation.mutateAsync({ item, settlementDate });
    },
    [markPaidMutation],
  );

  return {
    postings,
    templates,
    yearPostings,
    loading,
    refreshing,
    categories,
    sud,
    naturezas,
    initialBalance,
    loadTransactions,
    loadYearlyData,
    savePosting,
    deletePosting,
    saveCategories,
    saveInitialBalance,
    addSubcategory,
    addGroup,
    addSudValue,
    addNatureza,
    markPaid,
    linkBlingId,
    createChildPosting,
    saveTemplate,
    saveTemplateLabel,
  };
}
